import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { catchError, finalize, forkJoin, map, of, switchMap } from 'rxjs';

import { AdminDashboardPageService } from '../admin-dashboard-page.service';
import { ImageService } from '../../../../shared/services/image.service';
import { PATH } from '../../../../../assets/constants';
import { LoadMaskComponent } from '../../../../shared/load-mask/load-mask.component';
import { ICompany } from '../../../../../assets/types/dtoTypes';
import { CreateCompanyModalComponent } from '../../../../modals/create-company-modal/create-company-modal.component';

type SortKey = 'recent' | 'name' | 'jobOffers';

type CompanyRow = ICompany & {
  logoUrl?: string;
  jobOffersCount?: number;
  city?: string;
  country?: string;
};

@Component({
  selector: 'app-company-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, LoadMaskComponent],
  templateUrl: './company-page.component.html',
  styleUrls: ['./company-page.component.scss'],
})
export class CompanyPageComponent implements OnInit, OnDestroy {
  private readonly adminService = inject(AdminDashboardPageService);
  private readonly imageService = inject(ImageService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  protected readonly Set = Set;
  protected readonly Math = Math;
  isLoading = true;

  companies = signal<CompanyRow[]>([]);
  totalCompanies = signal<number>(0);

  searchTerm = signal<string>('');
  withJobOfferOnly = signal<boolean>(false);
  selectedCountry = signal<string>('');
  selectedIndustries = signal<Set<string>>(new Set());
  sortBy = signal<SortKey>('recent');

  page = signal<number>(1);
  pageSize = signal<number>(10);

  openMenuIndex = signal<number | null>(null);

  countries = signal<string[]>([]);
  industries = signal<string[]>([]);

  private readonly onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') this.openMenuIndex.set(null);
  };

  ngOnInit(): void {
    this.loadCompanies();
    window.addEventListener('keydown', this.onKeyDown);
  }

  ngOnDestroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
  }

  loadCompanies(): void {
    this.isLoading = true;

    this.adminService
      .getAllComapanies()
      .pipe(
        switchMap((companies: ICompany[]) => {
          this.totalCompanies.set(companies.length);

          const companiesWithExtras$ = companies.map((c: CompanyRow) => {
            const logoUrl = c.logoUrl || (c as ICompany).logoUrl || '';

            const jobOffersCount$ = this.adminService.getAllJobOffersByCompany(c.id).pipe(
              map((offers) => offers.length),
              catchError(() => of(0))
            );

            const logoWithSas$ = logoUrl
              ? this.imageService.appendSasTokenToCompanyImage(logoUrl, c.id).pipe(
                map((updatedUrl) => updatedUrl),
                catchError(() => of(logoUrl))
              )
              : of(logoUrl);

            return forkJoin({ logoUrl: logoWithSas$, jobOffersCount: jobOffersCount$ }).pipe(
              map(({ logoUrl, jobOffersCount }) => ({
                ...c,
                logoUrl,
                jobOffersCount,
              }))
            );
          });

          return companiesWithExtras$.length ? forkJoin(companiesWithExtras$) : of([]);
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (rows: CompanyRow[]) => {
          this.companies.set(rows);
          this.countries.set(
            this.distinct(rows.map((x) => (x.country || (x as ICompany).location || '').toString()))
          );
          this.industries.set(
            this.distinct(rows.map((x) => (x.industry || '').toString()))
          );
        },
        error: (err) => console.error('Error loading companies', err),
      });
  }

  filteredCompanies = computed((): CompanyRow[] => {
    const term = this.searchTerm().trim().toLowerCase();
    const withOffers = this.withJobOfferOnly();
    const country = this.selectedCountry();
    const inds = this.selectedIndustries();

    let list = [...this.companies()];

    if (term) {
      list = list.filter((c) => {
        const name = (c.name || (c as any).companyName || '').toString();
        const email = (c as ICompany).partnerEmail?.toString() || '';
        const website = (c as ICompany).website?.toString() || '';
        return [name, email, website].some((v) => v.toLowerCase().includes(term));
      });
    }

    if (withOffers) {
      list = list.filter((c) => (c.jobOffersCount ?? 0) > 0);
    }

    if (country) {
      list = list.filter((c) => (c.country || (c as ICompany).location) === country);
    }

    if (inds.size) {
      list = list.filter((c) => inds.has((c.industry || '').toString()));
    }

    const sort = this.sortBy();
    list.sort((a, b) => {
      switch (sort) {
        case 'name': {
          const an = (a.name || '').toString().toLowerCase();
          const bn = (b.name || '').toString().toLowerCase();
          return an.localeCompare(bn);
        }
        case 'jobOffers':
          return (b.jobOffersCount ?? 0) - (a.jobOffersCount ?? 0);
        default: {
          const ad = new Date((a as any).createdAt || (a as any).registrationDate || 0).getTime();
          const bd = new Date((b as any).createdAt || (b as any).registrationDate || 0).getTime();
          return bd - ad;
        }
      }
    });

    return list;
  });

  paginatedCompanies = computed(() => {
    const p = this.page();
    const size = this.pageSize();
    const start = (p - 1) * size;
    return this.filteredCompanies().slice(start, start + size);
  });

  toggleIndustry(label: string): void {
    const s = new Set(this.selectedIndustries());
    s.has(label) ? s.delete(label) : s.add(label);
    this.selectedIndustries.set(s);
    this.page.set(1);
  }

  clearFilters(): void {
    this.withJobOfferOnly.set(false);
    this.selectedCountry.set('');
    this.selectedIndustries.set(new Set());
    this.searchTerm.set('');
    this.sortBy.set('recent');
    this.page.set(1);
  }

  prevPage(): void {
    if (this.page() > 1) this.page.update((v) => v - 1);
  }

  nextPage(): void {
    const total = this.filteredCompanies().length;
    const maxPage = Math.max(1, Math.ceil(total / this.pageSize()));
    if (this.page() < maxPage) this.page.update((v) => v + 1);
  }

  viewCompany(id: string): void {
    this.router.navigate([PATH.COMPANY_DASHBOARD, id]);
  }

  editCompany(row: CompanyRow): void {
    alert(`(Demo) Edit company ${row.name || ''}`);
  }

  archiveCompany(row: CompanyRow): void {
    alert(`(Demo) Archive company ${row.name || ''}`);
  }

  companyLogo(c: CompanyRow): string {
    return c.logoUrl || 'assets/company-placeholder.svg';
  }

  addressOf(c: ICompany): string {
    const country = c.location;
    return  country || '—';
  }

  registeredAt(c: any): string {
    const date = new Date(c.createdAt || c.registrationDate || Date.now());
    const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' };
    const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `${date.toLocaleDateString(undefined, options)} - ${time}`;
  }

  jobOfferSummary(c: CompanyRow): string {
    const n = c.jobOffersCount ?? 0;
    if (!n) return 'No Job Offer';
    if (n === 1) return '1 job offer';
    return `${n} job offers`;
  }

  private distinct(arr: string[]): string[] {
    return [...new Set(arr.filter((a) => !!a.trim()))].sort((a, b) => a.localeCompare(b));
  }

  resetIndustries(): void {
    this.selectedIndustries.set(new Set());
    this.page.set(1);
  }

  openCreateCompanyDialog(): void {
    const ref = this.dialog.open(CreateCompanyModalComponent, {
      autoFocus: false,
      restoreFocus: false,
      disableClose: true,
      data: {},
    });

    ref.afterClosed().subscribe((result) => {
      if (result?.created) {
        this.loadCompanies();
      }
    });
  }


}
